import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const LOGO_URL = "/api/uploads/brand/logo.png";
const SESSION_KEY = "hexxium_intro_seen";
const TOTAL_MS = 2400;

const shouldSkip = () => {
  try {
    if (sessionStorage.getItem(SESSION_KEY)) return true;
  } catch {
    return true;
  }
  if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return true;
  return false;
};

export default function IntroSplash() {
  const [visible, setVisible] = useState(() => !shouldSkip());

  useEffect(() => {
    if (!visible) return;
    document.body.style.overflow = "hidden";
    // Hard failsafe: the intro can never block the site
    const t = setTimeout(() => {
      try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* storage unavailable */ }
      setVisible(false);
    }, TOTAL_MS);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = "";
    };
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="intro"
          className="fixed inset-0 z-[10000] flex flex-col items-center justify-center px-6"
          style={{ background: "radial-gradient(120% 120% at 50% 42%, #16181d 0%, #0b0c0f 55%, #060708 100%)" }}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.55, ease: "easeInOut" } }}
          data-testid="intro-splash"
        >
          <motion.img
            src={LOGO_URL}
            alt="Hexxium Research"
            className="w-[300px] sm:w-[420px] md:w-[520px] h-auto select-none pointer-events-none"
            style={{ filter: "drop-shadow(0 0 28px rgba(148,163,184,0.18)) drop-shadow(0 0 60px rgba(96,125,160,0.10))" }}
            initial={{ opacity: 0, scale: 1.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
            onError={() => setVisible(false)}
            draggable={false}
          />
          <div className="flex items-baseline gap-3 sm:gap-4 mt-2 sm:mt-3" data-testid="intro-tagline">
            {["Science.", "Purity.", "Results."].map((word, i) => (
              <motion.span
                key={word}
                className="metallic-text font-semibold tracking-[0.18em] uppercase text-sm sm:text-lg md:text-xl"
                style={{ textShadow: "0 0 22px rgba(148,163,184,0.25)" }}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + i * 0.18, duration: 0.45, ease: "easeOut" }}
              >
                {word}
              </motion.span>
            ))}
          </div>
          <motion.div
            className="absolute bottom-0 left-0 right-0 h-px"
            style={{ background: "linear-gradient(90deg, transparent, rgba(148,163,184,0.35), transparent)" }}
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ delay: 0.5, duration: 0.9, ease: "easeOut" }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
